import React, { Fragment, useCallback } from 'react';
import './ScreeningTypeSelector.scss';
import { OptionType } from '../../form/FormField';
import { getAllowedScreeningTypes } from './helper';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { SalvCheckbox, SalvDropdown, SalvNumberField, SalvSwitch } from '@salvapp/ui-components';
import { useMe } from '../../organisation/useMe';
import {
  InternalApiListSelectorGroup,
  InternalApiScreeningListType,
  InternalApiScreeningManualSearchRecordEntityTypeEnum,
  InternalApiScreeningMatchType,
} from '../../api-internal';
import { getListGroupLabel } from '../configure/new/helper';

const SANCTION_SCREENING_MATCH_TYPES = [
  InternalApiScreeningMatchType.FUZZY_MATCH,
  InternalApiScreeningMatchType.STARTS_WITH,
  InternalApiScreeningMatchType.EXACT_MATCH,
] as const;

interface SelectorProps {
  selectedTypes: InternalApiScreeningListType[];
  setSelectedTypes: (types: InternalApiScreeningListType[]) => void;
  searchAllProfiles: boolean;
  setSearchAllProfiles: (search: boolean) => void;
  screeningSelectorGroups: InternalApiListSelectorGroup[] | undefined;
  selectorGroupCodes: string[];
  setSelectorGroupCodes: (codes: string[]) => void;
  recordEntityTypeOption: OptionType | undefined;
  setRecordEntityTypeOption: (option: OptionType) => void;
  score: number | undefined;
  setScore: (score: number) => void;
  prefix: number | undefined;
  setPrefix: (prefix: number) => void;
  matchType: InternalApiScreeningMatchType | undefined;
  setMatchType: (value?: InternalApiScreeningMatchType) => void;
}

export const SCREENING_SEARCH_MATCH_ENTITY_TYPES = [
  {
    key: 'ALL',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.ALL,
    label: 'Individual & Business',
  },
  {
    key: 'PERSON',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.PERSON,
    label: 'Individual',
  },
  {
    key: 'ENTITY',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.ENTITY,
    label: 'Business',
  },
];

export const ScreeningTypeSelectorV2 = ({
  selectedTypes,
  setSelectedTypes,
  searchAllProfiles,
  setSearchAllProfiles,
  screeningSelectorGroups,
  selectorGroupCodes,
  setSelectorGroupCodes,
  recordEntityTypeOption,
  setRecordEntityTypeOption,
  score,
  setScore,
  prefix,
  setPrefix,
  matchType,
  setMatchType,
}: SelectorProps) => {
  const { features } = useMe();
  const isAdverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);

  const updateTypes = (type: InternalApiScreeningListType) => {
    let newTypes: InternalApiScreeningListType[];
    if (selectedTypes.includes(type)) {
      newTypes = selectedTypes.filter((t) => t !== type);
    } else {
      newTypes = [...selectedTypes, type];
    }
    setSelectedTypes(newTypes);
  };

  const screeningTypeToOptionType = (matchType: InternalApiScreeningMatchType): OptionType => {
    return {
      key: matchType,
      label: renderScreeningTypeLabel(matchType),
      value: matchType,
    };
  };

  const updateSelectorGroupCodes = useCallback(
    (groupCode: string) => (selected: boolean) => {
      if (selected) {
        setSelectorGroupCodes([...selectorGroupCodes, groupCode]);
      } else {
        setSelectorGroupCodes([...selectorGroupCodes.filter((code) => code !== groupCode)]);
      }
    },
    [selectorGroupCodes, setSelectorGroupCodes]
  );

  const renderScreeningTypeLabel = (screeningMatchType: InternalApiScreeningMatchType): string => {
    switch (screeningMatchType) {
      case InternalApiScreeningMatchType.FUZZY_MATCH:
        return 'Fuzzy';
      case InternalApiScreeningMatchType.STARTS_WITH:
        return 'Prefix';
      case InternalApiScreeningMatchType.EXACT_MATCH:
        return 'Exact';
      case InternalApiScreeningMatchType.CONTAINS:
        return 'Contains';
      case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        return 'Fuzzy Contains';
    }
  };

  const renderDisplayName = (type: InternalApiScreeningListType) => {
    if (type === InternalApiScreeningListType.SANCTION) {
      return 'Sanctions';
    } else if (type === InternalApiScreeningListType.PEP) {
      return 'PEP';
    } else if (type === InternalApiScreeningListType.ADVERSE_MEDIA) {
      return 'Adverse media';
    } else if (type === InternalApiScreeningListType.ADVERSE_MEDIA_ENTITY) {
      return 'Adverse media entity';
    }
  };

  const renderSanctionSelectors = () => (
    <div className='p-x-8'>
      <SalvSwitch
        className='m-b-8'
        label='All profiles'
        selected={searchAllProfiles}
        onChange={setSearchAllProfiles}
        disabled={searchAllProfiles}
      />
      {screeningSelectorGroups?.map((group) => (
        <SalvCheckbox
          key={group.code}
          className='m-b-8'
          label={getListGroupLabel(group)}
          selected={selectorGroupCodes.includes(group.code)}
          onChange={updateSelectorGroupCodes(group.code)}
        />
      ))}
    </div>
  );

  const renderList = (type: InternalApiScreeningListType) => {
    return (
      <React.Fragment key={type}>
        <SalvSwitch
          className='m-b-8'
          label={renderDisplayName(type)}
          selected={selectedTypes.includes(type)}
          onChange={() => updateTypes(type)}
        />
        {type === InternalApiScreeningListType.SANCTION &&
          selectedTypes.includes(InternalApiScreeningListType.SANCTION) &&
          renderSanctionSelectors()}
      </React.Fragment>
    );
  };

  return (
    <Fragment>
      <div className='w-100 mb-4'>
        <SalvDropdown
          label='Match type'
          options={SANCTION_SCREENING_MATCH_TYPES.map(screeningTypeToOptionType)}
          value={matchType ? screeningTypeToOptionType(matchType) : undefined}
          clearable={false}
          onChange={([option]) => {
            setMatchType(option.value as InternalApiScreeningMatchType);
          }}
        />
      </div>
      {matchType === InternalApiScreeningMatchType.FUZZY_MATCH && (
        <div className='w-100 mb-4'>
          <SalvDropdown
            label={'Search for a'}
            options={SCREENING_SEARCH_MATCH_ENTITY_TYPES}
            value={recordEntityTypeOption || undefined}
            clearable={false}
            onChange={([option]) => {
              setRecordEntityTypeOption(option);
            }}
          />
          <SalvNumberField label='Score' value={score} onChange={setScore} minValue={90} maxValue={100} />
        </div>
      )}
      {matchType === InternalApiScreeningMatchType.STARTS_WITH && (
        <div className='w-100 mb-4'>
          <SalvNumberField label='Prefix' value={prefix} onChange={setPrefix} minValue={1} maxValue={8} />
        </div>
      )}
      {getAllowedScreeningTypes(isAdverseMediaEntitiesEnabled).map(renderList)}
    </Fragment>
  );
};

export default ScreeningTypeSelectorV2;
